// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Firestore Sync Service
// ═══════════════════════════════════════════════════════════════════════════
// Syncs favorites and user data to Firebase Firestore
// ═══════════════════════════════════════════════════════════════════════════

import {
    doc,
    setDoc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    deleteDoc,
    writeBatch,
    serverTimestamp,
    orderBy,
    limit,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase.js';
import { getCurrentUser, onAuthChange } from './auth.js';
import * as localStorage from './storage.js';

// ─────────────────────────────────────────────────────────────────────────
// FAVORITES SYNC
// ─────────────────────────────────────────────────────────────────────────

let favoritesUnsubscribe = null;
let cachedFavorites = [];
let favoritesListeners = [];

// Initialize sync when user logs in
onAuthChange((user) => {
    if (user) {
        startFavoritesSync(user.uid);
    } else {
        stopFavoritesSync();
    }
});

function startFavoritesSync(userId) {
    if (favoritesUnsubscribe) {
        favoritesUnsubscribe();
    }

    const favoritesRef = collection(db, 'users', userId, 'favorites');
    const q = query(favoritesRef, orderBy('savedAt', 'desc'));

    favoritesUnsubscribe = onSnapshot(q, (snapshot) => {
        cachedFavorites = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Notify listeners
        favoritesListeners.forEach(cb => cb(cachedFavorites));

        // Also sync to localStorage as backup
        localStorage.syncFromCloud(cachedFavorites);
    }, (error) => {
        console.error('Favorites sync error:', error);
    });

    // Merge local favorites on first login
    mergeLocalFavorites(userId);
}

function stopFavoritesSync() {
    if (favoritesUnsubscribe) {
        favoritesUnsubscribe();
        favoritesUnsubscribe = null;
    }
    cachedFavorites = [];
}

export function onFavoritesChange(callback) {
    favoritesListeners.push(callback);
    // Immediately call with current data
    callback(cachedFavorites);

    return () => {
        favoritesListeners = favoritesListeners.filter(cb => cb !== callback);
    };
}

export function getFavorites() {
    const user = getCurrentUser();
    if (user && cachedFavorites.length > 0) {
        return cachedFavorites;
    }
    // Fall back to localStorage
    return localStorage.getFavorites();
}

// ─────────────────────────────────────────────────────────────────────────
// SAVE / REMOVE FAVORITES
// ─────────────────────────────────────────────────────────────────────────

export async function saveFavorite(verse) {
    const user = getCurrentUser();

    // Always save locally first (for offline support)
    localStorage.saveFavorite(verse);

    if (!user) return { success: true, local: true };

    try {
        const favoriteRef = doc(db, 'users', user.uid, 'favorites', verse.reference.replace(/[^a-zA-Z0-9]/g, '_'));

        await setDoc(favoriteRef, {
            reference: verse.reference,
            text: verse.text,
            book: verse.book || null,
            chapter: verse.chapter || null,
            verseNum: verse.verseNum || null,
            category: verse.category || null,
            theme: verse.theme || null,
            savedAt: serverTimestamp(),
            sharedPublicly: false
        });

        return { success: true, synced: true };
    } catch (error) {
        console.error('Save favorite error:', error);
        return { success: true, local: true, error: error.message };
    }
}

export async function removeFavorite(reference) {
    const user = getCurrentUser();

    // Always remove locally
    localStorage.removeFavorite(reference);

    if (!user) return { success: true, local: true };

    try {
        const favoriteId = reference.replace(/[^a-zA-Z0-9]/g, '_');
        const favoriteRef = doc(db, 'users', user.uid, 'favorites', favoriteId);
        await deleteDoc(favoriteRef);

        return { success: true, synced: true };
    } catch (error) {
        console.error('Remove favorite error:', error);
        return { success: true, local: true, error: error.message };
    }
}

export function isFavorite(reference) {
    const user = getCurrentUser();

    if (user && cachedFavorites.length > 0) {
        return cachedFavorites.some(f => f.reference === reference);
    }

    return localStorage.isFavorite(reference);
}

// ─────────────────────────────────────────────────────────────────────────
// MERGE LOCAL FAVORITES ON LOGIN
// ─────────────────────────────────────────────────────────────────────────

async function mergeLocalFavorites(userId) {
    const localFavorites = localStorage.getFavorites();

    if (localFavorites.length === 0) return;

    try {
        const batch = writeBatch(db);
        let addedCount = 0;

        for (const verse of localFavorites) {
            const favoriteId = verse.reference.replace(/[^a-zA-Z0-9]/g, '_');
            const favoriteRef = doc(db, 'users', userId, 'favorites', favoriteId);

            // Check if already exists
            const existing = cachedFavorites.find(f => f.reference === verse.reference);
            if (!existing) {
                batch.set(favoriteRef, {
                    reference: verse.reference,
                    text: verse.text,
                    book: verse.book || null,
                    chapter: verse.chapter || null,
                    verseNum: verse.verseNum || null,
                    category: verse.category || null,
                    theme: verse.theme || null,
                    savedAt: serverTimestamp(),
                    sharedPublicly: false
                });
                addedCount++;
            }
        }

        if (addedCount > 0) {
            await batch.commit();
            console.log(`Merged ${addedCount} local favorites to cloud`);
        }
    } catch (error) {
        console.error('Merge favorites error:', error);
    }
}

// ─────────────────────────────────────────────────────────────────────────
// COMMUNITY: SHARED VERSES
// ─────────────────────────────────────────────────────────────────────────

export async function shareVersePublicly(verse, isAnonymous = false) {
    const user = getCurrentUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
        const sharedRef = doc(collection(db, 'sharedVerses'));

        await setDoc(sharedRef, {
            reference: verse.reference,
            text: verse.text,
            sharedBy: isAnonymous ? null : user.uid,
            sharedByName: isAnonymous ? 'Anonymous' : (user.displayName || 'A friend'),
            sharedAt: serverTimestamp(),
            likes: 0,
            likedBy: []
        });

        // Update user's favorite to mark as shared
        const favoriteId = verse.reference.replace(/[^a-zA-Z0-9]/g, '_');
        const favoriteRef = doc(db, 'users', user.uid, 'favorites', favoriteId);
        await setDoc(favoriteRef, { sharedPublicly: true }, { merge: true });

        return { success: true, shareId: sharedRef.id };
    } catch (error) {
        console.error('Share verse error:', error);
        return { success: false, error: error.message };
    }
}

export async function getTrendingVerses(limitCount = 20) {
    try {
        const sharedRef = collection(db, 'sharedVerses');
        // Get more verses than needed, then shuffle and limit
        const q = query(sharedRef, limit(limitCount * 3));
        const snapshot = await getDocs(q);

        const verses = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Fisher-Yates shuffle for true randomness
        for (let i = verses.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [verses[i], verses[j]] = [verses[j], verses[i]];
        }

        return verses.slice(0, limitCount);
    } catch (error) {
        console.error('Get random verses error:', error);
        return [];
    }
}

export async function getRecentSharedVerses(limitCount = 50) {
    try {
        const sharedRef = collection(db, 'sharedVerses');
        const q = query(sharedRef, orderBy('sharedAt', 'desc'), limit(limitCount));
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Get recent shared error:', error);
        return [];
    }
}

export async function likeSharedVerse(verseId) {
    const user = getCurrentUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
        const verseRef = doc(db, 'sharedVerses', verseId);
        const verseSnap = await getDoc(verseRef);

        if (!verseSnap.exists()) {
            return { success: false, error: 'Verse not found' };
        }

        const data = verseSnap.data();
        const likedBy = data.likedBy || [];

        if (likedBy.includes(user.uid)) {
            // Unlike
            await setDoc(verseRef, {
                likes: (data.likes || 1) - 1,
                likedBy: likedBy.filter(id => id !== user.uid)
            }, { merge: true });
            return { success: true, liked: false };
        } else {
            // Like
            await setDoc(verseRef, {
                likes: (data.likes || 0) + 1,
                likedBy: [...likedBy, user.uid]
            }, { merge: true });
            return { success: true, liked: true };
        }
    } catch (error) {
        console.error('Like verse error:', error);
        return { success: false, error: error.message };
    }
}

// ─────────────────────────────────────────────────────────────────────────
// FRIENDS SYSTEM (Contact-based)
// ─────────────────────────────────────────────────────────────────────────

export async function addFriend(friendUserId) {
    const user = getCurrentUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
        // Add to my friends list
        const myFriendRef = doc(db, 'users', user.uid, 'friends', friendUserId);
        await setDoc(myFriendRef, {
            addedAt: serverTimestamp(),
            status: 'added'
        });

        return { success: true };
    } catch (error) {
        console.error('Add friend error:', error);
        return { success: false, error: error.message };
    }
}

export async function getFriendsSharedVerses(limitCount = 30) {
    const user = getCurrentUser();
    if (!user) return [];

    try {
        // Get friend IDs
        const friendsRef = collection(db, 'users', user.uid, 'friends');
        const friendsSnap = await getDocs(friendsRef);
        const friendIds = friendsSnap.docs.map(doc => doc.id);

        if (friendIds.length === 0) return [];

        // Get shared verses from friends
        const sharedRef = collection(db, 'sharedVerses');
        const q = query(
            sharedRef,
            where('sharedBy', 'in', friendIds.slice(0, 10)), // Firestore limit
            orderBy('sharedAt', 'desc'),
            limit(limitCount)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Get friends verses error:', error);
        return [];
    }
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT
// ─────────────────────────────────────────────────────────────────────────

export default {
    getFavorites,
    saveFavorite,
    removeFavorite,
    isFavorite,
    onFavoritesChange,
    shareVersePublicly,
    getTrendingVerses,
    getRecentSharedVerses,
    likeSharedVerse,
    addFriend,
    getFriendsSharedVerses
};
